import { useEffect, useCallback, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import BarcodeScannerFull from '../../../../../common/components/barcodeScanner/BarcodeScannerFull';
import { confirmReceivedFromWarecloud } from '../../../../services';
import {
  setNotification,
  setToogleNotification,
} from '../../../../../common/components/QRNotification/notificationQRSlice';
import { setOrdersModalConfig, setScanQROrder } from '../../../../orderSlice';
import useMobile from '../../../../../../hooks/useMobile';
import { LoadingBackdrop } from '../../../../../common';
import { useHandleScanValidationError } from '../../hooks';
import { getOrdersToPickup } from '../../../../services';
import { validateOrderByQR } from '../../../infra/api';
import { useValidateOrderByQR } from '../../../application/hooks';

const isNaturaOrder = (ecommerceName) => {
  return (ecommerceName || '')
    ?.replace(/\s+/g, '')
    ?.toLowerCase()
    .trim()
    .includes('natura');
};

const normalizeNaturaBarcode = (barcode) => {
  if (!barcode || typeof barcode !== 'string') return barcode;

  if (barcode.length === 13) {
    return barcode.slice(0, -3);
  }

  return barcode;
};

const compareBarcodeValues = (
  orderScanValue,
  scannedCodeValue,
  ecommerceName
) => {
  const orderValue = String(orderScanValue);
  const scannedValue = String(scannedCodeValue);

  if (isNaturaOrder(ecommerceName)) {
    const normalizedOrderValue = normalizeNaturaBarcode(orderValue);
    const normalizedScannedValue = normalizeNaturaBarcode(scannedValue);
    return normalizedOrderValue === normalizedScannedValue;
  }

  return orderValue === scannedValue;
};

export default function ReceiveOrderBarcodeScannerViewer({ open, onClose }) {
  const { t } = useTranslation(['title', 'button', 'validation']);
  const { scanQROrder, ordersToRetrieveFilters } = useSelector(
    (store) => store.orders
  );
  const isMobile = useMobile();
  const dispatch = useDispatch();

  const [selectOpen, setSelectOpen] = useState(false);
  const [selectOptions, setSelectOptions] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedOrderIsQR, setSelectedOrderIsQR] = useState(false);
  const [duplicateCodeInfo, setDuplicateCodeInfo] = useState({
    code: '',
    count: 0,
  });
  const [isResolvingDuplicate, setIsResolvingDuplicate] = useState(false);
  const [isNaturaFlow, setIsNaturaFlow] = useState(false);
  const [scannedOrders, setScannedOrders] = useState([]);
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);

  const showNotification = useCallback(
    (message, type = 'error', color = 'error') => {
      dispatch(setNotification({ message, type, color }));
      dispatch(setToogleNotification(true));
    },
    [dispatch]
  );

  const playBeep = useCallback(async () => {
    try {
      const audio = new Audio('/beep.mp3');
      audio.volume = 1;
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        await playPromise;
      }
    } catch (_) {
      return undefined;
    }
  }, []);

  const handleClose = useCallback(() => {
    if (isNaturaFlow && scannedOrders.length > 0) {
      dispatch(
        setNotification({
          message: t('barcode.natura_orders_processed', {
            ns: 'validation',
            count: scannedOrders.length,
          }),
          type: 'success',
          color: 'success',
        })
      );
      dispatch(setToogleNotification(true));
    }

    dispatch(setScanQROrder(null));
    onClose();
  }, [dispatch, onClose, isNaturaFlow, scannedOrders.length, t]);

  const processNaturaOrder = useCallback(
    async (order) => {
      try {
        await confirmReceivedFromWarecloud({
          id_order: order.id_order,
          comment: 'Procesado automáticamente por escáner Natura',
          id_order_transfer_confirmation: order.id_order_transfer_confirmation,
          barcode: true,
        });

        await playBeep();

        showNotification(
          t('barcode.natura_order_processed', {
            ns: 'validation',
            orderCode: order.order_code,
          }),
          'success',
          'success'
        );

        setScannedOrders((prev) => [
          ...prev,
          { ...order, barcode: true, processed: true },
        ]);
        setIsNaturaFlow(true);
        setIsProcessingOrder(false);
      } catch (error) {
        showNotification(
          t('barcode.natura_processing_error', { ns: 'validation' })
        );
        setIsProcessingOrder(false);
      }
    },
    [showNotification, t, playBeep]
  );

  const processNormalOrder = useCallback(
    async (orderId, isQR = false) => {
      try {
        const validationResult = await validateOrderByQR(orderId);
        const orderData = validationResult?.data;

        await playBeep();

        dispatch(
          setOrdersModalConfig({
            titleOpening: t('orders.retrieve.modal.wish_pickup_order', {
              ns: 'description',
            }),
            show: true,
            order: {
              ...orderData,
              ...(isQR ? { qr_code: true } : { barcode: true }),
            },
            modalType: 'toRetrieve',
          })
        );

        handleClose();
      } catch (error) {
        showNotification(t('barcode.processing_error', { ns: 'validation' }));
        setIsProcessingOrder(false);
      }
    },
    [dispatch, t, showNotification, handleClose, playBeep]
  );

  useEffect(() => {
    if (open) {
      dispatch(setScanQROrder(null));
      setScannedOrders([]);
      setIsNaturaFlow(false);
      setIsProcessingOrder(false);
      setSelectedOrderIsQR(false);
    }
  }, [open, dispatch]);

  const { data, isInitialLoading, isSuccess } = useValidateOrderByQR({
    idOrder: scanQROrder,
  });

  useHandleScanValidationError({ handleClose });

  useEffect(() => {
    if (
      isSuccess &&
      !selectOpen &&
      !isResolvingDuplicate &&
      !isProcessingOrder &&
      !isNaturaFlow
    ) {
      const orderData = data?.data;

      playBeep();

      dispatch(
        setOrdersModalConfig({
          titleOpening: t('orders.retrieve.modal.wish_pickup_order', {
            ns: 'description',
          }),
          show: true,
          order: { ...orderData, barcode: true },
          modalType: 'toRetrieve',
        })
      );

      handleClose();
    }
  }, [
    isSuccess,
    dispatch,
    t,
    data,
    handleClose,
    selectOpen,
    isResolvingDuplicate,
    isProcessingOrder,
    isNaturaFlow,
    playBeep,
  ]);

  const handleBarcodeDetected = useCallback(
    async (code) => {
      if (selectOpen || isResolvingDuplicate || isProcessingOrder) {
        return;
      }

      setIsProcessingOrder(true);

      try {
        const scannedCode = typeof code === 'string' ? code : code?.text || '';
        let scanLink = null;

        try {
          scanLink = new URL(scannedCode);
        } catch (_) {
          scanLink = null;
        }

        const id_order = scanLink?.searchParams.get('data');

        if (id_order !== null && id_order !== undefined) {
          const { page, id_warecloud } = ordersToRetrieveFilters || {
            page: 1,
            id_warecloud: '',
          };
          try {
            const resp = await getOrdersToPickup({
              page: page || 1,
              filter: '',
              id_warecloud: id_warecloud || '',
            });
            const list = Array.isArray(resp?.data?.orders)
              ? resp.data.orders
              : [];

            const current = list.find(
              (o) => Number(o.id_order) === Number(id_order)
            );

            if (current && current.scan_value) {
              const isValidCode = compareBarcodeValues(
                current.scan_value,
                id_order,
                current.ecommerce_name
              );

              if (!isValidCode) {
                dispatch(
                  setNotification({
                    message: t('barcode.invalid_code_with_value', {
                      ns: 'validation',
                      code: scannedCode,
                    }),
                    type: 'error',
                    color: 'error',
                  })
                );
                dispatch(setToogleNotification(true));
                setIsProcessingOrder(false);
                return;
              }
            }

            if (current?.order_code) {
              const sameCode = list.filter(
                (o) => String(o.order_code) === String(current.order_code)
              );

              if (sameCode.length > 1) {
                setSelectOptions(sameCode);
                setSelectedOrderId(Number(id_order));
                setSelectedOrderIsQR(true);
                setDuplicateCodeInfo({
                  code: String(current.order_code),
                  count: sameCode.length,
                });
                setIsResolvingDuplicate(true);
                setSelectOpen(true);
                setIsProcessingOrder(false);

                return;
              }
            }

            const currentOrder = list.find(
              (o) => Number(o.id_order) === Number(id_order)
            );

            if (currentOrder) {
              if (isNaturaOrder(currentOrder.ecommerce_name)) {
                await processNaturaOrder(currentOrder);
              } else {
                await processNormalOrder(id_order, true);
              }
            } else {
              showNotification(
                t('barcode.invalid_code_with_value', {
                  ns: 'validation',
                  code: scannedCode,
                })
              );
              setIsProcessingOrder(false);
            }
            return;
          } catch (error) {
            console.error(error);
            showNotification(
              t('barcode.processing_error_with_value', {
                ns: 'validation',
                code: scannedCode,
              })
            );
            setIsProcessingOrder(false);
            return;
          }
        }

        const codeValue = scannedCode.trim();
        if (!codeValue) {
          showNotification(
            t('barcode.invalid_code_with_value', {
              ns: 'validation',
              code: scannedCode,
            })
          );
          setIsProcessingOrder(false);
          return;
        }

        const { page, id_warecloud } = ordersToRetrieveFilters || {
          page: 1,
          id_warecloud: '',
        };

        let list = [];
        try {
          const resp = await getOrdersToPickup({
            page: page || 1,
            filter: '',
            id_warecloud: id_warecloud || '',
          });
          list = Array.isArray(resp?.data?.orders) ? resp.data.orders : [];
        } catch (error) {
          console.error('Error fetching orders for barcode scan:', error);
          showNotification(
            t('barcode.processing_error_with_value', {
              ns: 'validation',
              code: codeValue,
            })
          );
          setIsProcessingOrder(false);
          return;
        }

        const matches = list.filter((o) =>
          compareBarcodeValues(o.scan_value, codeValue, o.ecommerce_name)
        );

        if (matches.length === 0) {
          showNotification(
            t('barcode.invalid_code_with_value', {
              ns: 'validation',
              code: codeValue,
            })
          );
          setIsProcessingOrder(false);
          return;
        }

        if (matches.length === 1) {
          const currentOrder = matches[0];

          if (isNaturaOrder(currentOrder.ecommerce_name)) {
            await processNaturaOrder(currentOrder);
          } else {
            await processNormalOrder(currentOrder.id_order, false);
          }
          return;
        }

        if (matches.length > 1) {
          setSelectOptions(matches);
          setSelectedOrderId(matches[0]?.id_order || null);
          setSelectedOrderIsQR(false);
          setDuplicateCodeInfo({ code: codeValue, count: matches.length });
          setIsResolvingDuplicate(true);
          setSelectOpen(true);
          setIsProcessingOrder(false);
        }
      } catch (error) {
        showNotification(t('barcode.processing_error', { ns: 'validation' }));
        setIsProcessingOrder(false);
        setIsResolvingDuplicate(false);
      }
    },
    [
      dispatch,
      t,
      ordersToRetrieveFilters,
      selectOpen,
      isResolvingDuplicate,
      isProcessingOrder,
      showNotification,
      processNaturaOrder,
      processNormalOrder,
    ]
  );

  if (isInitialLoading || isProcessingOrder) {
    return (
      <div>
        <LoadingBackdrop
          loadingComponent={<CircularProgress color="inherit" />}
        />
      </div>
    );
  }

  return (
    <Dialog open={open || selectOpen} fullScreen={isMobile}>
      <DialogContent>
        <Typography
          component="h1"
          variant="h1"
          color="primary"
          sx={{ mt: 4, mb: 2, textAlign: 'center' }}
        >
          {t('barcode_code', { ns: 'title' })}
        </Typography>

        <Typography component="p" variant="body1" mb={4}>
          {t('barcode_code_label', { ns: 'description' })}
        </Typography>

        {!selectOpen && (
          <BarcodeScannerFull onDetected={handleBarcodeDetected} />
        )}

        {selectOpen && (
          <Dialog open={selectOpen} onClose={() => {}} maxWidth="xs" fullWidth>
            <DialogContent>
              <Typography
                component="h2"
                variant="h6"
                mb={1}
                sx={{ fontWeight: 'bold' }}
              >
                {t('select_order_scanned', {
                  ns: 'description',
                  defaultValue: '¿Cuál pedido estás escaneando?',
                })}
              </Typography>
              <Typography
                component="p"
                variant="body2"
                mb={2}
                color="text.secondary"
              >
                {t('duplicated_code_detected', {
                  ns: 'description',
                  defaultValue: 'Se detectó el código duplicado',
                })}
                : <strong>{duplicateCodeInfo.code}</strong> ·{' '}
                {t('options', { ns: 'description', defaultValue: 'opciones' })}:{' '}
                {duplicateCodeInfo.count}
              </Typography>
              {selectOptions.map((opt, index) => (
                <div
                  key={`${opt.id_order}-${opt.ecommerce_name}-${index}`}
                  style={{ marginBottom: 8 }}
                >
                  <label
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <input
                      type="radio"
                      name="orderOption"
                      checked={selectedOrderId === opt.id_order}
                      onChange={() => setSelectedOrderId(opt.id_order)}
                    />
                    <span>
                      {`${opt.ecommerce_name} - ${opt.order_code}`}
                      {opt.tracking_code && `|${opt.tracking_code}`}
                    </span>
                  </label>
                </div>
              ))}
              <Button
                variant="contained"
                fullWidth
                onClick={async () => {
                  if (selectedOrderId) {
                    setIsProcessingOrder(true);
                    setSelectOpen(false);
                    setIsResolvingDuplicate(false);

                    const selectedOrder = selectOptions.find(
                      (opt) => opt.id_order === selectedOrderId
                    );

                    if (isNaturaOrder(selectedOrder.ecommerce_name)) {
                      await processNaturaOrder(selectedOrder);
                    } else {
                      await processNormalOrder(
                        selectedOrderId,
                        selectedOrderIsQR
                      );
                    }
                  }
                }}
                sx={{ mt: 1 }}
              >
                {t('confirm', { ns: 'button', defaultValue: 'Confirmar' })}
              </Button>
            </DialogContent>
          </Dialog>
        )}

        {!selectOpen && (
          <Button
            variant="contained"
            fullWidth
            color="secondary"
            onClick={onClose}
            sx={{ color: 'inherit', marginTop: 2 }}
          >
            {t('return', { ns: 'button' })}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
